import { jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useMemo } from 'react';
import { useNavigationManager, NavigationManagerProvider } from './hook.manager.js';

function InMemoryNavigationProvider(props) {
  const subscribersRef = useRef(/* @__PURE__ */ new Set());
  const historyRef = useRef([]);
  const managerContext = useMemo(() => {
    return {
      getPath: () => {
        const path = historyRef.current[historyRef.current.length - 1] ?? props.initialPath;
        return path.replace(props.basePath ?? "", "");
      },
      subscribe: (cb) => {
        const uniqueCb = () => cb();
        subscribersRef.current.add(uniqueCb);
        return () => subscribersRef.current.delete(uniqueCb);
      },
      push: (nextPath) => {
        const resolvedNextPath = `${props.basePath ?? ""}${nextPath}`;
        if (historyRef.current[historyRef.current.length - 1] === resolvedNextPath) {
          return;
        }
        historyRef.current = [...historyRef.current, resolvedNextPath];
        subscribersRef.current.forEach((cb) => cb());
      },
      replace: (nextPath) => {
        const resolvedNextPath = `${props.basePath ?? ""}${nextPath}`;
        historyRef.current = [
          ...historyRef.current.slice(0, -1),
          resolvedNextPath
        ];
        subscribersRef.current.forEach((cb) => cb());
      },
      back: () => {
        historyRef.current = [...historyRef.current.slice(0, -1)];
        subscribersRef.current.forEach((cb) => cb());
      },
      enhanceLink: (linkProps, ref) => /* @__PURE__ */ jsx(
        InMemoryLink,
        {
          ref,
          ...linkProps,
          basePath: props.basePath,
          href: linkProps.href
        }
      )
    };
  }, [props.basePath, props.initialPath]);
  return /* @__PURE__ */ jsx(NavigationManagerProvider, { manager: managerContext, children: props.children });
}
const InMemoryLink = forwardRef(function InMemoryLink2({ basePath, ...props }, ref) {
  const navigation = useNavigationManager();
  const onClick = (evt) => {
    var _a;
    (_a = props.onClick) == null ? void 0 : _a.call(props, evt);
    if (!props.href) {
      return;
    }
    if (evt.isDefaultPrevented()) {
      return;
    }
    if (evt.metaKey || evt.ctrlKey) {
      return;
    }
    if (props.target === "_blank") {
      return;
    }
    const isAbsoluteHref = props.href ? canParseUrl(props.href) : false;
    if (isAbsoluteHref) {
      const isAbsoluteHrefToSameDomain = new URL(props.href).origin === window.location.origin;
      if (!isAbsoluteHrefToSameDomain) {
        return;
      }
      evt.preventDefault();
      navigation.push(new URL(props.href).href);
    } else {
      evt.preventDefault();
      navigation.push(props.href);
    }
  };
  const href = (() => {
    const isAbsolute = props.href ? canParseUrl(props.href) : false;
    if (isAbsolute) {
      return props.href;
    }
    return applyBasePath(basePath, props.href ?? "");
  })();
  return /* @__PURE__ */ jsx("a", { ref, ...props, href, onClick, children: props.children });
});
function applyBasePath(basePath, path) {
  if (!basePath || path.startsWith(basePath)) {
    return path;
  }
  return `${basePath}${path}`;
}
function canParseUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export { InMemoryNavigationProvider };
//# sourceMappingURL=integration.in-memory.js.map
